import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";
import { openSquareBooking } from "@/lib/api";

const PHOTO = "https://customer-assets-v7afamib.emergentagent.net/job_clip-reserve-14/artifacts/b9oq7uz7_20170621_161329.webp";

const highlights = [
  { v: "8 Yrs", l: "U.S. Army Veteran" },
  { v: "19 Yrs", l: "Master Barber" },
  { v: "3×", l: "National Champions" },
  { v: "21 Yrs", l: "Married" },
];

const story = [
  "Christian Ortiz is a 40-year-old U.S. Army veteran, entrepreneur, barber, coach, husband, and father whose life has been built around service, hard work, family, and giving back to the community that helped shape him.",
  "Born in Puerto Rico and raised in Worcester, Massachusetts, Christian attended Doherty Memorial High School, where his passion for athletics, competition, and teamwork continued to grow. That competitive spirit stayed with him well into adulthood. Christian played football until the age of 37, competing at the semi-professional level with the Orlando Rage, Kansas Spartans, and Marlborough Shamrocks. During his time with the Shamrocks, he was part of an incredible run that resulted in three National Championships — an accomplishment that represents years of dedication, sacrifice, and brotherhood.",
  "Christian also proudly served eight years in the United States Army, an experience that strengthened the discipline, leadership, loyalty, and commitment to others that continue to guide him today.",
  "Professionally, Christian has spent 19 years as a barber, building relationships with people from all walks of life and taking pride in a career centered around community and personal connection.",
  "But his proudest accomplishments are found at home. Christian has been married to his wife for 21 years, and together they have raised two children — one currently in high school and the other beginning his freshman year of college. For Christian, being a husband and father has always been at the center of everything he works for.",
  "Today, Christian has taken his lifelong love of sports and mentorship and turned it into an opportunity to serve the next generation. He owns and operates a baseball program and training facility in Worcester, where the mission goes far beyond winning games. His program focuses on developing young athletes, building character, creating opportunities, and helping players pursue their dreams of playing collegiate baseball.",
  "From Puerto Rico to Worcester, from the Army to the football field, from behind the barber chair to mentoring young athletes, Christian's journey has always been about hard work, perseverance, family, and service.",
];

export default function MeetBarber() {
  const book = () => openSquareBooking();

  return (
    <div className="grain relative bg-barber-bg min-h-screen">
      {/* top bar */}
      <header className="sticky top-0 z-50 bg-barber-bg/95 border-b border-barber-gold/20 backdrop-blur">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" data-testid="barber-home-link" className="flex items-center">
            <img src="/barber-parlor-logo.png" alt="The Barber Parlor" className="h-12 sm:h-14 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="hidden sm:flex items-center gap-2 text-sm text-barber-muted hover:text-barber-gold transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to site
            </Link>
            <Button data-testid="barber-book-btn" onClick={book} className="btn-gold rounded-none font-semibold tracking-wide">
              Book Now
            </Button>
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="relative overflow-hidden radial-green">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="uppercase tracking-[0.35em] text-barber-gold text-xs mb-5">Meet The Barber</p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-barber-cream leading-[0.95] tracking-tight">
              Christian
              <br />
              <span className="italic text-barber-gold">Ortiz</span>
            </h1>
            <p className="mt-6 text-lg text-barber-cream/80 leading-relaxed max-w-xl">
              Veteran. Master barber of 19 years. Coach, husband, and father. Every cut is
              backed by a lifetime of discipline, service, and genuine connection.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {["U.S. Army Veteran", "Born in Puerto Rico", "Worcester, MA"].map((t) => (
                <span key={t} className="border border-barber-gold/30 bg-barber-surface/60 px-4 py-2 text-sm text-barber-cream">
                  {t}
                </span>
              ))}
            </div>
            <Button data-testid="barber-hero-book" onClick={book} className="btn-gold rounded-none font-semibold text-base px-8 py-6 mt-9 tracking-wide">
              Book with Christian
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-barber-gold/25" />
            <img
              src={PHOTO}
              alt="Christian Ortiz"
              className="relative w-full max-h-[640px] object-cover object-top border border-barber-gold/20"
            />
          </motion.div>
        </div>
      </section>

      {/* stats */}
      <section className="bg-[#0C120C] border-y border-barber-gold/15">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {highlights.map((s, i) => (
            <motion.div
              key={s.l}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="text-center lg:text-left"
            >
              <div className="font-heading text-5xl sm:text-6xl font-bold text-barber-amber leading-none">{s.v}</div>
              <div className="mt-3 text-xs uppercase tracking-[0.25em] text-barber-muted">{s.l}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* story */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="flex items-center gap-3 mb-10">
          <span className="h-px w-12 bg-barber-gold" />
          <p className="uppercase tracking-[0.35em] text-barber-gold text-xs">His Story</p>
        </div>
        <div className="space-y-6">
          {story.map((p, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={i === 0 ? "text-barber-cream text-xl leading-relaxed font-heading" : "text-barber-muted text-base leading-relaxed"}
            >
              {p}
            </motion.p>
          ))}
        </div>

        {/* pull quote */}
        <div className="mt-14 border-l-2 border-barber-gold pl-6 py-2">
          <p className="font-heading text-2xl sm:text-3xl text-barber-cream leading-snug">
            "Success means using everything I've learned to help the next generation believe
            their dreams are possible — and giving them the tools to achieve them."
          </p>
          <div className="mt-4 flex items-center gap-2">
            {[...Array(5)].map((_, j) => (
              <Star key={j} className="w-4 h-4 fill-[#58B033] text-[#58B033]" />
            ))}
            <span className="text-barber-muted text-sm uppercase tracking-widest ml-1">Christian Ortiz</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-barber-surface border-t border-barber-gold/20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl text-barber-cream">Sit in the chair.</h2>
            <p className="text-barber-muted mt-2">Experience the cut that keeps Worcester coming back.</p>
          </div>
          <Button data-testid="barber-cta-book" onClick={book} className="btn-gold rounded-none font-semibold px-10 py-6 tracking-wide">
            Book Appointment
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
